import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import GlobalFilters from "../components/GlobalFilters";
import { useLang } from "../lib/i18n";
import { KpiCard, PageHeader, pct, num } from "../lib/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Percent, Clock, AlertTriangle, Copy, ShieldAlert, FileWarning } from "lucide-react";

export default function DataQuality() {
  const [filters, setFilters] = useState({});
  const { t } = useLang();
  const { data, isLoading } = useQuery({
    queryKey: ["dq", filters],
    queryFn: async () => (await api.get("/data-quality", { params: filters })).data,
  });

  return (
    <div data-testid="data-quality-page">
      <PageHeader title={t("Contrôle Qualité des Données")} subtitle={t("Complétude, promptitude, cohérence, doublons et valeurs aberrantes")} />
      <GlobalFilters value={filters} onChange={setFilters} />
      {isLoading ? <p className="text-slate-400 text-sm">{t("Chargement…")}</p> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <KpiCard testid="dq-completeness" label={t("Complétude")} value={pct(data.completeness)} icon={Percent} tone="teal" />
            <KpiCard testid="dq-timeliness" label={t("Promptitude")} value={pct(data.timeliness)} icon={Clock} tone="teal" />
            <KpiCard testid="dq-score" label={t("Score qualité moyen")} value={data.avg_quality_score ?? "N/A"} icon={ShieldAlert} />
            <KpiCard testid="dq-issues" label={t("Rapports à anomalies")} value={num(data.reports_with_issues)} icon={FileWarning} tone="red" />
            <KpiCard testid="dq-dups" label={t("Doublons potentiels")} value={num(data.duplicate_count)} icon={Copy} tone="amber" />
            <KpiCard testid="dq-outliers" label={t("Valeurs aberrantes")} value={num(data.outlier_count)} icon={AlertTriangle} tone="amber" />
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">{t("Complétude par Zone de Santé")}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.completeness_by_zone} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="health_zone" tick={{ fontSize: 10 }} width={110} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar dataKey="completeness" name={t("Complétude %")} fill="#0284C7" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">{t("Rapports avec anomalies de cohérence")}</h3>
              <div className="overflow-y-auto max-h-[300px]">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50"><TableHead>{t("Date")}</TableHead><TableHead>{t("Zone")}</TableHead><TableHead>{t("Score")}</TableHead><TableHead>{t("Anomalies")}</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.flagged_reports.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">{t("Aucune anomalie détectée.")}</TableCell></TableRow> :
                      data.flagged_reports.map((r) => (
                        <TableRow key={r.id}><TableCell className="font-mono text-xs">{r.date}</TableCell><TableCell className="text-sm">{r.health_zone}</TableCell>
                          <TableCell><span className="text-red-600 font-semibold text-sm">{r.score}</span></TableCell>
                          <TableCell className="text-xs text-red-600">{r.flags.join("; ")}</TableCell></TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {data.outliers.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 mt-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">{t("Valeurs aberrantes (alertes reçues vs plage attendue par zone)")}</h3>
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>{t("Date")}</TableHead><TableHead>{t("Zone")}</TableHead><TableHead className="text-right">{t("Valeur")}</TableHead><TableHead className="text-right">{t("Plage attendue")}</TableHead></TableRow></TableHeader>
                <TableBody>{data.outliers.map((o) => (
                  <TableRow key={o.id}><TableCell className="font-mono text-xs">{o.date}</TableCell><TableCell className="text-sm">{o.health_zone}</TableCell>
                    <TableCell className="text-right font-semibold text-amber-600">{o.value}</TableCell><TableCell className="text-right text-slate-500">{o.expected_range}</TableCell></TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
